import React, { useState, useEffect } from 'react';
import { Table, Tag, Card, DatePicker, Select, Input, Button, Space, Drawer, Typography, Alert, Empty, Tooltip, Row, Col, Descriptions, Divider, message, Flex } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, DownloadOutlined, ClearOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import EmptyState from '../components/EmptyState';
import { HistoryOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Paragraph, Title } = Typography;

const ACTION_LABELS = {
    'LOGIN': 'User Login',
    'LOGIN_FAIL': 'Failed Login',
    'LOGOUT': 'User Logout',
    'CREATE_USER': 'Created User',
    'UPDATE_USER': 'Updated User',
    'DELETE_USER': 'Deleted User',
    'ENABLE_USER': 'Enabled User',
    'DISABLE_USER': 'Disabled User',
    'RESET_PASSWORD': 'Reset Password',
    'UPDATE_USER_PERMISSIONS': 'Updated Permissions',
    'PRODUCT_CREATE': 'Created Product',
    'PRODUCT_UPDATE': 'Updated Product',
    'PRODUCT_DELETE': 'Deleted Product',
    'STOCK_ADJUST': 'Adjusted Stock',
    'INVOICE_CREATE': 'Created Invoice',
    'INVOICE_VALIDATE': 'Finalized Invoice',
    'VALIDATE_INVOICE': 'Finalized Invoice',
    'RECORD_PAYMENT': 'Recorded Payment',
    'DELETE_INVOICE': 'Deleted Invoice',
    'BACKUP_CREATE_START': 'Backup Started',
    'BACKUP_DELETE': 'Deleted Backup',
    'RESTORE_START': 'Restore Started',
    'UPLOAD_RESTORE_EXEC': 'Uploaded Restore Executed',
    'RESTORE_FAILED': 'Restore Failed'
};

const Logs = () => {
    // State
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 25 });

    // Filters State
    const [filters, setFilters] = useState({
        user: undefined,
        action: undefined,
        module: undefined,
        status: undefined,
        q: undefined,
        dateRange: [],
        sort: 'created_at',
        order: 'DESC'
    });

    const [autoRefresh, setAutoRefresh] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(10); // seconds

    // UI State
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [users, setUsers] = useState([]);
    const [actions, setActions] = useState([]);
    const [error, setError] = useState(null);

    // Helpers
    const getModule = (event) => {
        if (!event) return 'System';
        if (event.startsWith('INVOICE_') || event.includes('PAYMENT')) return 'Invoices';
        if (event.startsWith('PRODUCT_') || event.startsWith('INVENTORY_')) return 'Inventory';
        if (event.startsWith('BACKUP_') || event.startsWith('RESTORE_')) return 'System';
        if (['LOGIN', 'LOGOUT', 'DISABLE_USER', 'CREATE_USER'].includes(event)) return 'Auth';
        return 'Other';
    };

    const StatusTag = ({ status }) => {
        let color = 'default';
        if (status === 'SUCCESS') color = 'success';
        if (status === 'FAIL' || status === 'ERROR') color = 'error';
        if (status === 'PENDING') color = 'processing';
        return <Tag color={color}>{status}</Tag>;
    };

    const fetchLogs = async (page = 1, pageSize = 25, currentFilters = filters) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                pageSize,
                user: currentFilters.user,
                action: currentFilters.action,
                status: currentFilters.status,
                q: currentFilters.q,
                sort: currentFilters.sort,
                order: currentFilters.order,
                module: currentFilters.module // Pass module to BFF
            };

            if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
                params.from = currentFilters.dateRange[0].format('YYYY-MM-DD');
                params.to = currentFilters.dateRange[1].format('YYYY-MM-DD');
            }

            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');

            const res = await axios.get('/api/logs', {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            setLogs(res.data.data || []);
            setMeta({
                total: res.data.meta.total || 0,
                page: res.data.meta.page || 1,
                pageSize: res.data.meta.pageSize || 25,
            });

            if (res.data.meta.distinctActors) setUsers(res.data.meta.distinctActors);
            if (res.data.meta.distinctActions) setActions(res.data.meta.distinctActions);

        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 401) {
                setError('Session expired. Please logout and login again.');
            } else {
                setError('Failed to load audit logs.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Debounce Search & Fetch
    useEffect(() => {
        fetchLogs(1, meta.pageSize, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, meta.pageSize]);

    // Auto Refresh Effect
    useEffect(() => {
        let interval;
        if (autoRefresh && !loading) {
            interval = setInterval(() => {
                fetchLogs(meta.page, meta.pageSize, filters);
            }, refreshInterval * 1000);
        }
        return () => clearInterval(interval);
    }, [autoRefresh, refreshInterval, loading, meta.page, meta.pageSize, filters]);

    // Handlers
    const handleTableChange = (pagination, filtersArg, sorter) => {
        const newFilters = { ...filters };

        if (sorter.field) {
            newFilters.sort = sorter.field;
            newFilters.order = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        } else {
            newFilters.sort = 'created_at';
            newFilters.order = 'DESC';
        }

        setFilters(newFilters);
        // fetchLogs handled by useEffect logic or direct call? 
        // Sync state update triggers effect, but we also want page update.
        // Let's rely on explicit call for page change to avoid double fetch with effect.
        // Actually, since filters changed, effect will fire. 
        // We just need to ensure `page` is updated in fetch.
    };

    const onPageChange = (page, pageSize) => {
        setMeta(prev => ({ ...prev, page, pageSize }));
        fetchLogs(page, pageSize, filters);
    };

    const handleFilterChange = (key, val) => {
        setFilters(prev => ({ ...prev, [key]: val }));
    };

    const handleDatePreset = (type) => {
        let range = [];
        const today = dayjs();
        if (type === 'today') range = [today, today];
        else if (type === 'yesterday') range = [today.subtract(1, 'day'), today.subtract(1, 'day')];
        else if (type === 'week') range = [today.subtract(6, 'day'), today];
        else if (type === 'month') range = [today.subtract(29, 'day'), today];
        else if (type === 'this_month') range = [today.startOf('month'), today.endOf('month')];

        handleFilterChange('dateRange', range);
    };

    const clearFilters = () => {
        setFilters({
            user: undefined,
            action: undefined,
            module: undefined,
            status: undefined,
            q: undefined,
            dateRange: [],
            sort: 'created_at',
            order: 'DESC'
        });
    };

    const handleExport = async () => {
        try {
            message.loading({ content: 'Exporting...', key: 'export' });
            const token = localStorage.getItem('token');
            const params = {
                user: filters.user,
                action: filters.action,
                status: filters.status,
                q: filters.q,
                sort: filters.sort,
                order: filters.order
            };
            if (filters.dateRange && filters.dateRange.length === 2) {
                params.from = filters.dateRange[0].format('YYYY-MM-DD');
                params.to = filters.dateRange[1].format('YYYY-MM-DD');
            }

            const response = await axios.get('/api/logs/export', {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit-logs-${dayjs().format('YYYY-MM-DD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success({ content: 'Export complete', key: 'export' });
        } catch (err) {
            message.error({ content: 'Export failed', key: 'export' });
        }
    };

    const columns = [
        {
            title: 'Time',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            sorter: true,
            defaultSortOrder: 'descend',
            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: 'Module',
            key: 'module',
            width: 100,
            render: (_, r) => <Tag color="blue">{getModule(r.event)}</Tag>
        },
        {
            title: 'User',
            dataIndex: 'actor',
            key: 'actor',
            width: 140,
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Action',
            dataIndex: 'event',
            key: 'event',
            width: 200,
            render: (text) => (
                <Tooltip title={`Raw code: ${text}`}>
                    <Text strong>{ACTION_LABELS[text] || text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            sorter: true,
            render: (status) => <StatusTag status={status} />
        },
        {
            title: 'IP',
            key: 'ip',
            width: 130,
            render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{r.details?.ip || '-'}</Text>
        },
        {
            title: 'Details',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedLog(record); setDrawerVisible(true); }}>
                    View
                </Button>
            )
        }
    ];

    // Layout fix: Centered container
    return (
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: 0 }}>
            {/* Header Actions */}
            <Flex justify="space-between" align="center" style={{ marginBottom: 32 }}>
                <div>
                    <div className="section-title">Audit</div>
                    <Title level={2} style={{ margin: 0, fontWeight: 700 }}>System Logs</Title>
                    <Text type="secondary">Detailed audit trail of all administrative and user activities</Text>
                </div>
                <Space size="middle">
                    <Flex align="center" style={{ marginRight: 16 }}>
                        <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>AUTO-REFRESH (10s):</Text>
                        <Button
                            size="small"
                            type={autoRefresh ? "primary" : "default"}
                            onClick={() => setAutoRefresh(!autoRefresh)}
                        >
                            {autoRefresh ? "ON" : "OFF"}
                        </Button>
                    </Flex>
                    <Button icon={<ClearOutlined />} onClick={clearFilters} disabled={loading}>Clear Filters</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(meta.page, meta.pageSize)}>Refresh</Button>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} style={{ height: '40px' }}>Export CSV</Button>
                </Space>
            </Flex>

            {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} action={<Button size="small" onClick={() => fetchLogs(meta.page, meta.pageSize)}>Retry</Button>} />}

            {/* Sticky Filters Toolbar */}
            <Card
                className="premium-card"
                style={{ marginBottom: 24, position: 'sticky', top: 0, zIndex: 100 }}
                bodyStyle={{ padding: '16px 24px' }}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {/* Presets Row */}
                    <Space size="small" wrap>
                        <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>QUICK DATES:</Text>
                        <Tag.CheckableTag checked={false} onChange={() => handleDatePreset('today')}>Today</Tag.CheckableTag>
                        <Tag.CheckableTag checked={false} onChange={() => handleDatePreset('yesterday')}>Yesterday</Tag.CheckableTag>
                        <Tag.CheckableTag checked={false} onChange={() => handleDatePreset('week')}>Last 7 Days</Tag.CheckableTag>
                        <Tag.CheckableTag checked={false} onChange={() => handleDatePreset('month')}>Last 30 Days</Tag.CheckableTag>
                        <Tag.CheckableTag checked={false} onChange={() => handleDatePreset('this_month')}>This Month</Tag.CheckableTag>
                    </Space>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* Inputs Row */}
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <RangePicker
                                style={{ width: '100%' }}
                                value={filters.dateRange}
                                onChange={(dates) => handleFilterChange('dateRange', dates)}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={4} lg={4}>
                            <Select
                                placeholder="All Users"
                                allowClear
                                style={{ width: '100%' }}
                                value={filters.user}
                                onChange={(val) => handleFilterChange('user', val)}
                                showSearch
                            >
                                {users.map(u => <Option key={u} value={u}>{u}</Option>)}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={5} lg={5}>
                            <Select
                                placeholder="All Actions"
                                allowClear
                                style={{ width: '100%' }}
                                value={filters.action}
                                onChange={(val) => handleFilterChange('action', val)}
                                showSearch
                            >
                                {actions.map(a => <Option key={a} value={a}>{a}</Option>)}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={3} lg={3}>
                            <Select
                                placeholder="Status"
                                allowClear
                                style={{ width: '100%' }}
                                value={filters.status}
                                onChange={(val) => handleFilterChange('status', val)}
                            >
                                <Option value="SUCCESS">Success</Option>
                                <Option value="FAIL">Fail</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={7}>
                            <Input
                                placeholder="Search details..."
                                prefix={<SearchOutlined />}
                                value={filters.q}
                                onChange={(e) => handleFilterChange('q', e.target.value)}
                                allowClear
                            />
                        </Col>
                    </Row>
                </Space>
            </Card>

            <Table
                className="premium-card"
                style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}
                columns={columns}
                dataSource={logs}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: meta.page,
                    pageSize: meta.pageSize,
                    total: meta.total,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    onChange: onPageChange
                }}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
                size="middle"
                locale={{
                    emptyText: <EmptyState
                        message="No logs found"
                        description="Adjust your filters or search terms to find system activity."
                        icon={<HistoryOutlined />}
                    />
                }}
            />

            <Drawer
                title="Log Details"
                placement="right"
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                width={640}
                extra={
                    <Button onClick={() => setDrawerVisible(false)}>Close</Button>
                }
            >
                {selectedLog && (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        {/* Summary Header */}
                        <Alert
                            message={
                                <Space>
                                    <StatusTag status={selectedLog.status} />
                                    <Text strong>{selectedLog.event}</Text>
                                    <Text type="secondary">by {selectedLog.actor}</Text>
                                </Space>
                            }
                            type={selectedLog.status === 'SUCCESS' ? 'success' : 'error'}
                            showIcon={false}
                            style={{ padding: '8px 16px' }}
                        />

                        {/* Primary Metadata */}
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label="Timestamp" span={2}>{dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss dddd')}</Descriptions.Item>
                            <Descriptions.Item label="Actor">{selectedLog.actor}</Descriptions.Item>
                            <Descriptions.Item label="IP Address">{selectedLog.details?.ip || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Module"><Tag>{getModule(selectedLog.event)}</Tag></Descriptions.Item>
                            <Descriptions.Item label="Action">{selectedLog.event}</Descriptions.Item>
                        </Descriptions>

                        {/* Target User Section (If exists) */}
                        {selectedLog.details?.target && (
                            <Card size="small" title="Target User" style={{ borderColor: '#d9d9d9', background: '#fafafa' }}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Login"><Text strong>{selectedLog.details.target.login || 'N/A'}</Text></Descriptions.Item>
                                    <Descriptions.Item label="Email">{selectedLog.details.target.email || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="ID">{selectedLog.details.target.id}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        )}

                        {/* Additional Details (Filtered) */}
                        {(() => {
                            const { target, ip, userAgent, ...rest } = selectedLog.details || {};
                            if (Object.keys(rest).length === 0) return null;
                            return (
                                <Card size="small" title="Additional Details">
                                    {Object.entries(rest).map(([key, val]) => (
                                        <div key={key} style={{ marginBottom: 4 }}>
                                            <Text type="secondary" style={{ marginRight: 8 }}>{key}:</Text>
                                            <Text code>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</Text>
                                        </div>
                                    ))}
                                </Card>
                            );
                        })()}

                        {/* Footer Actions */}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(selectedLog.details, null, 2));
                                    message.success('Copied JSON');
                                }}
                            >
                                Copy Full JSON
                            </Button>
                        </div>
                    </Space>
                )}
            </Drawer>
        </div>
    );
};


export default Logs;
